import { Typography } from '@mui/material';
import useConfig from 'hooks/useConfig';
import {
    MRT_TableInstance,
    MaterialReactTable,
    useMaterialReactTable,
    MRT_ColumnFiltersState,
} from 'material-react-table';
import { memo, useEffect, useState } from 'react';
import {
    usePutApiUserPreferencesMutation,
    useGetApiUserPreferencesParsedQuery,
} from 'store/userOrg/enhancedUserOrgApi';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { CustomMaterialReactTableProps } from 'types/components';

const CustomMaterialReactTable = memo(
    ({
        manualPagination = false,
        manualFiltering = false,
        autoResetPageIndex = true,
        onGlobalFilterChange,
        enableExpanding,
        onPaginationChange,
        data,
        isLoading,
        enableRowSelection,
        pagination: paginationProps,
        searchText,
        searchPlaceholder,
        columns,
        showGlobalFilter = true,
        enableGrouping,
        handleRowSelectionChange,
        renderBottomToolbarCustomActions,
        rowActions,
        initialState,
        renderDetailPanel,
        renderTopToolbarCustomActions,
        resetRowSelection,
        muiTableBodyRowProps,
        totalItems,
        showColumnFilters,
        onColumnFiltersChange,
        columnFiltersProps,
        enableColumnFilters = true,
    }: CustomMaterialReactTableProps) => {
        const { data: preferences } = useGetApiUserPreferencesParsedQuery();
        const [putPreferences, { isLoading: putPreferencesIsLoading }] = usePutApiUserPreferencesMutation();
        const { tableOptions } = useConfig();
        const [rendered, setRendered] = useState(false);
        const [globalFilter, setGlobalFilter] = useState(searchText ?? '');
        const [density, setDensity] = useState(tableOptions?.density ?? 'spacious');
        const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(columnFiltersProps ?? []);
        const [pagination, setPagination] = useState(
            paginationProps ?? {
                pageIndex: 0,
                pageSize: 5,
            }
        );

        useEffect(() => {
            tableOptions?.density !== density &&
                putPreferences({
                    body: {
                        ...preferences,
                        tableOptions: { ...preferences?.tableOptions, density },
                    },
                });
        }, [density]);

        useEffect(() => {
            setRendered(true);
        }, []);

        useEffect(() => {
            if (searchText !== undefined && searchText !== globalFilter) {
                setGlobalFilter(searchText);
            }
        }, [searchText]);

        useEffect(() => {
            if (
                paginationProps &&
                (paginationProps.pageIndex !== pagination.pageIndex || paginationProps.pageSize !== pagination.pageSize)
            ) {
                setPagination(paginationProps);
            }
        }, [paginationProps]);

        useEffect(() => {
            if (rendered && onGlobalFilterChange && globalFilter !== searchText) {
                onGlobalFilterChange(globalFilter);
            }
        }, [globalFilter, onGlobalFilterChange, rendered, searchText]);

        useEffect(() => {
            if (rendered && onColumnFiltersChange) {
                onColumnFiltersChange(columnFilters);
            }
        }, [columnFilters, onColumnFiltersChange, rendered]);

        useEffect(() => {
            if (
                rendered &&
                onPaginationChange &&
                (pagination.pageIndex !== paginationProps?.pageIndex ||
                    pagination.pageSize !== paginationProps?.pageSize)
            ) {
                onPaginationChange(pagination);
            }
        }, [pagination, onPaginationChange, rendered, paginationProps]);

        const table = useMaterialReactTable({
            columns: columns,
            getRowId: row => row.id,
            getSubRows: row => row.subRows,
            enableExpanding: enableExpanding,
            paginateExpandedRows: false,
            data: rendered ? data : [],
            onGlobalFilterChange: setGlobalFilter,
            onPaginationChange: setPagination,
            enableGrouping: enableGrouping,
            enableRowActions: rowActions ? true : false,
            enableFullScreenToggle: false,
            enableRowSelection,
            enableDensityToggle: !putPreferencesIsLoading,
            positionActionsColumn: 'last',
            renderDetailPanel: renderDetailPanel,
            muiExpandButtonProps: ({ row, table }) => ({
                onClick: () => table.setExpanded({ [row.id]: !row.getIsExpanded() }),
            }),
            onColumnFiltersChange: setColumnFilters,
            renderRowActions: rowActions
                ? ({ row }) => [
                      rowActions({
                          closeMenu: () => {},
                          row: row,
                          staticRowIndex: undefined,
                          table: {} as MRT_TableInstance<any>,
                      }),
                  ]
                : () => [],

            onDensityChange: setDensity,
            mrtTheme: theme => ({
                baseBackgroundColor: theme.palette.background.paper,
            }),

            manualFiltering,
            autoResetPageIndex,
            state: {
                density,
                columnFilters,
                globalFilter: globalFilter,

                showGlobalFilter,
                pagination,
                isLoading,
            },
            initialState: {
                ...initialState,
                showGlobalFilter,
                density,
                pagination: paginationProps,
                showColumnFilters,
            },
            enableGlobalFilter: showGlobalFilter,
            enableColumnFilters,
            manualPagination,
            muiSearchTextFieldProps: {
                placeholder: searchPlaceholder ?? `Search ${totalItems ?? data.length} rows`,
            },
            muiTableProps: { sx: { overflow: 'hidden' } },
            muiPaginationProps: {
                color: 'primary',
                shape: 'rounded',
                showRowsPerPage: true,
                rowsPerPageOptions: [5, 10, 20, 50, 100],
                variant: 'outlined',
            },
            rowCount: totalItems,
            muiSelectCheckboxProps: {
                icon: <CheckBoxOutlineBlankIcon fontSize='large' />,
                checkedIcon: <CheckBoxIcon color='success' fontSize='large' />,
            },
            muiSelectAllCheckboxProps: {
                icon: <CheckBoxOutlineBlankIcon fontSize='large' />,
                checkedIcon: <CheckBoxIcon color='success' fontSize='large' />,
                indeterminateIcon: <IndeterminateCheckBoxIcon color='primary' fontSize='large' />,
            },
            muiTableBodyRowProps: ({ row }) => (muiTableBodyRowProps ? muiTableBodyRowProps(row) : {}),
            paginationDisplayMode: 'pages',
            renderTopToolbarCustomActions: renderTopToolbarCustomActions ? renderTopToolbarCustomActions : undefined,
            renderBottomToolbarCustomActions: () => (
                <>
                    {renderBottomToolbarCustomActions && renderBottomToolbarCustomActions}
                    <Typography>Total items: {totalItems ?? data.length}</Typography>
                </>
            ),
        });

        useEffect(() => {
            const selectedRowIds = Object.keys(table.getState()?.rowSelection)?.map(id => String(id));

            selectedRowIds?.length && handleRowSelectionChange && handleRowSelectionChange(selectedRowIds);
            !selectedRowIds?.length && handleRowSelectionChange && handleRowSelectionChange([]);
        }, [table?.getState()?.rowSelection]);

        useEffect(() => {
            resetRowSelection &&
                table.setState(prev => ({
                    ...prev,
                    rowSelection: {},
                }));
        }, [resetRowSelection]);

        return <MaterialReactTable table={table} />;
    }
);

export default CustomMaterialReactTable;
